import { useState, useCallback } from "react";
import { autoLabelGroups as autoLabelGroupsUtil } from "../../../utils/groupUtils";

const defaultColors = [
  "#1a6d51", // Dark Spring Green
  "#dc3545", // Danger Red
  "#0d6efd", // Primary Blue
  "#fd7e14", // Orange
  "#6f42c1", // Purple
  "#20c997", // Teal
  "#ffc107", // Warning Yellow
  "#d63384", // Pink
  "#6c757d", // Secondary Gray
  "#198754", // Success Green
  "#e83e8c", // Magenta
  "#17a2b8", // Info Cyan
  "#8b4513", // Saddle Brown (Earth tone)
  "#000080", // Navy (Deep dark blue)
  "#adff2f", // Green Yellow (High contrast bright)
  "#ba55d3", // Medium Orchid (Lighter purple)
  "#800000", // Maroon (Deep red)
  "#4682b4", // Steel Blue (Desaturated mid-blue)
  "#4b0082", // Indigo (Very dark purple)
  "#f08080", // Light Coral (Pastel red/pink)
  "#40e0d0", // Turquoise (Bright light cyan)
  "#a0522d", // Sienna (Reddish brown)
  "#87ceeb", // Sky Blue (Light pastel blue)
  "#b8860b", // Dark Goldenrod (Dark yellow/brown)
  "#556b2f", // Dark Olive Green (Dark yellow-green)
  "#ffb6c1", // Light Pink (Pastel pink)
  "#2f4f4f", // Dark Slate Gray (Dark green-gray)
  "#db7093", // Pale Violet Red (Muted pink-red)
  "#cd853f", // Peru (Warm light brown/tan)
  "#7cfc00", // Lawn Green (Neon green)
  "#d8bfd8", // Thistle (Pale gray-purple)
  "#ffdab9"  // Peach Puff (Very light orange/peach)
];

function removeSeqTypesFromSampleNames(str) {
  // Remove standalone numbers first
  const stripped_name = str
      .replace(/(^|_)\d+(_|$)/g, "$1$2")
      .replace(/__/g, "_")
      .replace(/^_|_$/g, "");
  return stripped_name
    .replace(
      /(^|[_\-\s.])(rpf|rna|rnaseq|riboseq|disomeseq|polysomeseq|trnaseq)(?=$|[_\-\s.])/gi,
      "$1"
    )
    .replace(/[_\-\s]+(?=\.)/g, "")
    .replace(/[_\-\s]{2,}/g, "_")
    .replace(/^[_\-\s]+|[_\-\s]+$/g, "");
}

function getFileName(file) {
  return file.file_name || file.name || file.filename || "";
}

function getGroupingName(file, { ignoreSeqTypes = false } = {}) {
  const name = getFileName(file);
  return ignoreSeqTypes ? removeSeqTypesFromSampleNames(name) : name;
}

export const useGroupsManager = () => {
  const [groups, setGroups] = useState([
    {
      id: 1,
      name: "Group 1",
      color: "#1a6d51",
      files: [],
      label: "Group 1",
    },
    {
      id: 2,
      name: "Group 2",
      color: "#dc3545",
      files: [],
      label: "Group 2",
    },
  ]);

  const setGroupsFromPayload = useCallback((payloadGroups) => {
    if (!payloadGroups) return;
    const groupEntries = Object.entries(payloadGroups);
    const nextGroups = groupEntries.map(([color, data], index) => ({
      id: index + 1,
      name: data.label || `Group ${index + 1}`,
      label: data.label || `Group ${index + 1}`,
      color: color || defaultColors[index % defaultColors.length],
      files: data.file_ids || [],
    }));
    setGroups(nextGroups);
  }, []);

  const addGroup = useCallback(() => {
    const newId = Math.max(...groups.map((g) => g.id), 0) + 1;
    const colorIndex = groups.length % defaultColors.length;
    const newGroupName = `Group ${newId}`;

    setGroups((prev) => [
      ...prev,
      {
        id: newId,
        name: newGroupName,
        label: newGroupName,
        color: defaultColors[colorIndex],
        files: [],
      },
    ]);
  }, [groups]);

  const removeGroup = useCallback((groupId) => {
    setGroups((prev) => prev.filter((g) => g.id !== groupId));
  }, []);

  const updateGroupName = useCallback((groupId, name) => {
    setGroups((prev) => {
      const groupExists = prev.find((group) => group.id === groupId);
      let updatedGroups = prev;

      if (!groupExists) {
        const newGroup = {
          id: groupId,
          name: name,
          label: name,
          color: defaultColors[(groupId - 1) % defaultColors.length],
          files: [],
        };
        updatedGroups = [...prev, newGroup];
      }

      return updatedGroups.map((g) =>
        g.id === groupId ? { ...g, name, label: name } : g
      );
    });
  }, []);

  const updateGroupColor = useCallback((groupId, color) => {
    setGroups((prev) => {
      const groupExists = prev.find((group) => group.id === groupId);
      let updatedGroups = prev;

      if (!groupExists) {
        const newGroup = {
          id: groupId,
          name: `Group ${groupId}`,
          label: `Group ${groupId}`,
          color: color,
          files: [],
        };
        updatedGroups = [...prev, newGroup];
      }

      return updatedGroups.map((g) => (g.id === groupId ? { ...g, color } : g));
    });
  }, []);

  const addFilesToGroup = useCallback((groupId, fileIds) => {
    const filesToAdd = Array.isArray(fileIds) ? fileIds : [fileIds];
    setGroups((prev) => {
      // Check if the group exists, if not create it
      const groupExists = prev.find((group) => group.id === groupId);
      let updatedGroups = prev;

      if (!groupExists) {
        const newGroup = {
          id: groupId,
          name: `Group ${groupId}`,
          label: `Group ${groupId}`,
          color: defaultColors[(groupId - 1) % defaultColors.length],
          files: [],
        };
        updatedGroups = [...prev, newGroup];
      }

      return updatedGroups.map((g) => {
        if (g.id === groupId) {
          const newFiles = filesToAdd.filter((id) => !g.files.includes(id));
          return { ...g, files: [...g.files, ...newFiles] };
        }
        return {
          ...g,
          files: g.files.filter((id) => !filesToAdd.includes(id)),
        };
      });
    });
  }, []);

  const removeFilesFromGroup = useCallback((groupId, fileIds) => {
    const filesToRemove = Array.isArray(fileIds) ? fileIds : [fileIds];
    setGroups((prev) =>
      prev.map((g) =>
        g.id === groupId
          ? { ...g, files: g.files.filter((id) => !filesToRemove.includes(id)) }
          : g
      )
    );
  }, []);

  const clearGroup = useCallback((groupId) => {
    setGroups((prev) => {
      const groupExists = prev.find((group) => group.id === groupId);
      let updatedGroups = prev;

      if (!groupExists) {
        const newGroup = {
          id: groupId,
          name: `Group ${groupId}`,
          label: `Group ${groupId}`,
          color: defaultColors[(groupId - 1) % defaultColors.length],
          files: [],
        };
        updatedGroups = [...prev, newGroup];
      }

      return updatedGroups.map((g) =>
        g.id === groupId ? { ...g, files: [] } : g
      );
    });
  }, []);

  // Helper function to find common parts in file names (matching original JS logic)
  const findCommonParts = useCallback((fileNames) => {
    if (fileNames.length === 0) return "";
    if (fileNames.length === 1) return fileNames[0];

    // Find the longest common prefix
    let commonPrefix = fileNames[0];
    for (let i = 1; i < fileNames.length; i++) {
      let j = 0;
      while (
        j < Math.min(commonPrefix.length, fileNames[i].length) &&
        commonPrefix[j] === fileNames[i][j]
      ) {
        j++;
      }
      commonPrefix = commonPrefix.substring(0, j);
      if (commonPrefix === "") break;
    }

    // If no common prefix, try to find common patterns
    if (commonPrefix.length < 2) {
      // Try to extract common patterns like sample names, conditions, etc.
      const patterns = fileNames.map((name) => {
        // Remove common file extensions
        name = name.replace(/\.(fastq|fq|bam|sam|txt|csv)$/i, "");

        // Try to extract meaningful parts (before numbers, underscores, etc.)
        const match = name.match(/^([A-Za-z]+)/);
        return match ? match[1] : name;
      });

      // Find most common pattern
      const patternCounts = {};
      patterns.forEach((pattern) => {
        patternCounts[pattern] = (patternCounts[pattern] || 0) + 1;
      });

      const mostCommonPattern = Object.keys(patternCounts).reduce((a, b) =>
        patternCounts[a] > patternCounts[b] ? a : b
      );

      return mostCommonPattern || "Group";
    }

    // Clean up the common prefix (remove trailing numbers, underscores, etc.)
    return commonPrefix.replace(/[_\-\d\s]+$/, "") || commonPrefix;
  }, []);

  // Use the common autoLabelGroups utility
  const autoLabelGroups = useCallback((files) => {
    if (!files || files.length === 0) return;

    setGroups((prev) => {
      const updatedGroups = autoLabelGroupsUtil(prev, files);
      return updatedGroups.map((group) => ({
        ...group,
        label: group.name, // Keep label in sync with name
      }));
    });
  }, []);

  // Helper function to find common parts in file names (matching original JS logic)
  const autoFillGroups = useCallback(
    (files, { ignoreSeqTypes = false } = {}) => {
      if (!files || files.length === 0) return;
      console.log("autofill groups called with", ignoreSeqTypes)

      const filesForGrouping = ignoreSeqTypes
        ? files.map((file) => {
            const groupingName = getGroupingName(file, { ignoreSeqTypes });

            return {
              ...file,
              file_name: groupingName,
              name: groupingName,
              filename: groupingName,
            };
          })
        : files;

      const filesDict = {};
      filesForGrouping.forEach((file) => {
        filesDict[file.file_id] = getFileName(file);
      });

      const similarGroups = groupBySimilarNames(filesDict);
      const similarGroupArrays = Object.values(similarGroups).map((arr) =>
        arr.map((id) => (typeof id === "string" ? parseInt(id) : id))
      );

      setGroups((prev) => {
        const currentAssigned = new Set(prev.flatMap((g) => g.files || []));

        // Only consider files not already assigned to any group
        const unassignedGroups = similarGroupArrays
          .map((arr) => arr.filter((id) => !currentAssigned.has(id)))
          .filter((arr) => arr.length > 0);

        if (unassignedGroups.length === 0) return prev;

        // Prepare target indices: first take empty groups, then add new groups as needed
        const nextGroups = [...prev];
        const emptyIndices = nextGroups
          .map((g, idx) => (g.files && g.files.length === 0 ? idx : -1))
          .filter((idx) => idx >= 0);

        let colorIndex = nextGroups.length % defaultColors.length;

        const ensureTargetIndex = () => {
          if (emptyIndices.length > 0) return emptyIndices.shift();
          // add new group
          const newId = Math.max(...nextGroups.map((g) => g.id), 0) + 1;
          const newName = `Group ${newId}`;
          const newColor = defaultColors[colorIndex % defaultColors.length];
          colorIndex += 1;
          nextGroups.push({
            id: newId,
            name: newName,
            label: newName,
            color: newColor,
            files: [],
          });
          return nextGroups.length - 1;
        };

        unassignedGroups.forEach((groupFiles) => {
          const targetIndex = ensureTargetIndex();
          const existing = nextGroups[targetIndex].files || [];
          // Fill without removing any existing assignments
          const merged = Array.from(new Set([...existing, ...groupFiles]));
          nextGroups[targetIndex] = {
            ...nextGroups[targetIndex],
            files: merged,
          };
        });

        return nextGroups;
      });

      // Auto-label after filling
      setTimeout(() => {
        autoLabelGroups(filesForGrouping);
      }, 0);
    },
    [autoLabelGroups]
  );

  // Helper functions (include these in your component or utils)
  function removeRepWithNumber(str) {
    return str.replace(/_?rep\d+/gi, "");
  }

  function removeStandaloneNumber(str) {
    return str
      .replace(/(^|_)\d+(_|$)/g, "$1$2")
      .replace(/__/g, "_")
      .replace(/^_|_$/g, "");
  }

  function groupBySimilarNames(files) {
    const keys = Object.keys(files);
    var groups = {};

    // This will be set to true if any two files are grouped together
    var any_group = false;
    for (let i = 0; i < keys.length; i++) {
      const id = keys[i];
      const name = files[id];
      const stripped_name = removeRepWithNumber(name);
      if (stripped_name in groups) {
        groups[stripped_name].push(id);
        any_group = true;
      } else {
        groups[stripped_name] = [id];
      }
    }

    if (!any_group) {
      // If it's failed to make any group at this stage do again but strip all standalone numbers
      groups = {};
      for (let i = 0; i < keys.length; i++) {
        const id = keys[i];
        const name = files[id];
        const stripped_name = removeStandaloneNumber(name);
        if (stripped_name in groups) {
          groups[stripped_name].push(id);
          any_group = true;
        } else {
          groups[stripped_name] = [id];
        }
      }
    }

    console.log(groups);
    return groups;
  }

  const clearAllGroups = useCallback(() => {
    setGroups((prev) =>
      prev.map((g, index) => ({
        ...g,
        files: [],
        name: `Group ${g.id}`,
        label: `Group ${g.id}`, // Reset to default name and label
      }))
    );
  }, []);

  // Alternative autoLabelGroups based on subgroups (previous implementation)
  const autoLabelGroupsBySubgroups = useCallback(
    (files) => {
      if (files.length === 0) return;

      const subgroups = [
        ...new Set(
          files.map((f) => f.subgroup || f.group || f.condition).filter(Boolean)
        ),
      ];

      if (subgroups.length === 0) {
        const patterns = files.map((f) => {
          const name = f.file_name || f.name || "";
          const match = name.match(/^([A-Za-z]+\d*)/);
          return match ? match[1] : "Unknown";
        });
        subgroups.push(...new Set(patterns));
      }

      while (groups.length < subgroups.length) {
        addGroup();
      }

      setGroups((prev) =>
        prev.map((group, index) => {
          if (index < subgroups.length) {
            const subgroupName = subgroups[index];
            const subgroupFiles = files
              .filter((f) => {
                const fileSubgroup = f.subgroup || f.group || f.condition;
                if (fileSubgroup) return fileSubgroup === subgroupName;

                const fileName = f.file_name || f.name || "";
                const match = fileName.match(/^([A-Za-z]+\d*)/);
                return match && match[1] === subgroupName;
              })
              .map((f) => f.file_id);

            return {
              ...group,
              name: subgroupName,
              label: subgroupName,
              files: subgroupFiles,
            };
          }
          return { ...group, files: [] };
        })
      );
    },
    [groups.length, addGroup]
  );

  const getMasterFileDict = useCallback(() => {
    const masterDict = {};
    groups.forEach((group) => {
      if (group.files.length > 0) {
        masterDict[group.color] = {
          file_ids: group.files,
          label: group.label || group.name,
        };
      }
    });
    return masterDict;
  }, [groups]);

  const validateGroupsForComparison = useCallback(() => {
    const errors = [];
    const groupsWithFiles = groups.filter((g) => g.files.length > 0);

    if (groupsWithFiles.length < 1) {
      errors.push("At least 1 group with files are required for comparison plot generation");
    }

    groupsWithFiles.forEach((group, index) => {
      if (!group.name.trim()) {
        errors.push(`Group ${index + 1} needs a name`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
      groupsWithFiles: groupsWithFiles.length,
    };
  }, [groups]);

  const getGroupsSummary = useCallback(() => {
    return {
      totalGroups: groups.length,
      groupsWithFiles: groups.filter((g) => g.files.length > 0).length,
      totalFilesAssigned: groups.reduce((sum, g) => sum + g.files.length, 0),
      groupDetails: groups.map((g) => ({
        id: g.id,
        name: g.name,
        color: g.color,
        fileCount: g.files.length,
      })),
    };
  }, [groups]);

  const resetGroups = useCallback(() => {
    setGroups([
      {
        id: 1,
        name: "Group 1",
        color: "#1a6d51",
        files: [],
        label: "Group 1",
      },
      {
        id: 2,
        name: "Group 2",
        color: "#dc3545",
        files: [],
        label: "Group 2",
      },
    ]);
  }, []);

  return {
    // State
    groups,
    setGroupsFromPayload,
    // Basic group management
    addGroup,
    removeGroup,
    updateGroupName,
    updateGroupColor,

    // File management
    addFilesToGroup,
    removeFilesFromGroup,
    clearGroup,
    clearAllGroups,

    // Auto functions
    autoFillGroups,
    autoLabelGroups, // This now matches original JS logic
    autoLabelGroupsBySubgroups, // Alternative implementation

    // Utility functions
    getMasterFileDict,
    validateGroupsForComparison,
    getGroupsSummary,
    resetGroups,
    findCommonParts, // Exposed for external use if needed

    // Computed values
    totalGroups: groups.length,
    groupsWithFiles: groups.filter((g) => g.files.length > 0).length,
    totalFilesAssigned: groups.reduce((sum, g) => sum + g.files.length, 0),
  };
};
